import { ActivatedRoute, ActivatedRouteSnapshot, ActivationEnd, NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

/**
 * Tracks the most nested child route snapshot via ActivationEnd events.
 * Uses a BehaviorSubject so late subscribers receive the current value.
 * Used by DetailsLayoutBaseComponent for MenuItem-based active item tracking.
 */
export function createChildRouteSnapshot$(router: Router): BehaviorSubject<ActivatedRouteSnapshot> {
    const subject$ = new BehaviorSubject<ActivatedRouteSnapshot>(null);

    router.events.pipe(
        filter((x): x is ActivationEnd => x instanceof ActivationEnd),
        map((x: ActivationEnd) => {
            let r = x.snapshot;
            while (r.firstChild) { r = r.firstChild; }
            return r;
        }),
        distinctUntilChanged()
    ).subscribe(x => subject$.next(x));

    return subject$;
}

/**
 * Creates an Observable<string> that emits the active child route path
 * relative to the given parent route, joining segments with '/'.
 *
 * Bind each p-tab value to `tab.routerLink.join('/')` (or just `tab.routerLink` for string links).
 *
 * @example
 * ```ts
 * this.activeRoute$ = createActiveRoute$(this.router, this.route);
 * ```
 */
export function createActiveRoute$(router: Router, route: ActivatedRoute): Observable<string> {
    const getChildPath = (): string => {
        const parentUrl = route.snapshot.pathFromRoot
            .reduce<string[]>((acc, seg) => [...acc, ...seg.url.map(u => u.path)], [])
            .join('/');
        const url = router.url.split(/[?#]/)[0].substring(1);
        return url.startsWith(parentUrl + '/') ? url.substring(parentUrl.length + 1) : '';
    };

    return router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => getChildPath()),
        startWith(getChildPath()),
        distinctUntilChanged()
    );
}
